// ===========================================
// Charm Promo Enforcement (Pierre + Welcome Bracelet)
// ===========================================

// Config: list of charm promos to enforce
const CHARM_PROMOS = [
  {
    variantId: "46790830882982",      // Pierre + welcome bracelet bundle
    requiredCode: "CHARMED2MEETU",    // Required discount code
  },
  {
    variantId: "46715440890022",      // Second charm promo variant
    requiredCode: "CHARMEDTOMEETYOU", // Required discount code
  },
];

const PROMO_VARIANT_IDS = CHARM_PROMOS.map((p) => p.variantId);

// -------------------------------------------
// NEW: Landing config for auto-adding GWP
// -------------------------------------------

const CHARM_LANDING = {
  // This is the second promo (bracelet-only)
  variantId: "46715440890022",
  requiredCode: "CHARMEDTOMEETYOU",

  // Where the Klaviyo links are allowed to send them
  // (add more paths here as needed)
  targetPaths: [
    "/collections/bestsellers",
    "/collections/craft-kits-for-beginners",
    "/collections/all-beginner-and-plus-crochet-kits",
    "/collections/collab",
    "/collections/bundles",
    "/collections/collectors-boxes",
    "/collections/bestsellers/products/penguin-crochet-kit",
    "/collections/bestsellers/products/dinosaur-crochet-kit",
    "/collections/bestsellers/products/easy-peasy-beginner-bundle",
    "/collections/bestsellers/products/fox-crochet-kit",
    "/collections/new-arrivals",
    "/collections/woobly-wonderland",
  ],

  // Hash flag, confirmed Klaviyo preserves
  hash: "#promo_charm_bestsellers",

  // Session flag so we don’t keep re-adding in the same session
  sessionFlag: "promo_charm_bestsellers_applied",
};


let firstCharmEnforcement = true;

// -------------------------------------------
// Shared helpers
// -------------------------------------------

async function getCart() {
  const res = await fetch("/cart.js", { credentials: "same-origin" });
  return res.json();
}

/**
 * If no promo item is present, just return the cart immediately.
 * If ANY promo item IS present, optionally wait for discount codes to settle.
 */
async function getCartWithOptionalWait(retries = 5, delay = 250) {
  const initialCart = await getCart();
  const hasPromoItem = initialCart.items.some((item) =>
    PROMO_VARIANT_IDS.includes(String(item.variant_id))
  );

  if (!hasPromoItem) {
    return initialCart;
  }

  // We do have at least one promo item — wait a bit for codes to appear, to avoid race conditions
  for (let i = 0; i < retries; i++) {
    const cart = await getCart();
    const hasCode =
      Array.isArray(cart.discount_codes) && cart.discount_codes.length > 0;
    if (hasCode) return cart;
    await new Promise((r) => setTimeout(r, delay));
  }

  // If we get here, codes never showed up — just return latest cart anyway
  return getCart();
}

// -------------------------------------------
// NEW: Auto-add GWP on collection landing
// -------------------------------------------
function isOnCharmLandingPath() {
  try {
    const currentPath = (window.location.pathname || "").replace(/\/+$/, "");

    return CHARM_LANDING.targetPaths.some((rawPath) => {
      const base = (rawPath || "").replace(/\/+$/, "");

      // Match base path exactly or any deeper sub-path like /collections/.../page/2
      return (
        currentPath === base ||
        currentPath.startsWith(base + "/")
      );
    });
  } catch (e) {
    console.warn("[CharmPromo] Unable to evaluate landing path", e);
    return false;
  }
}

function cleanCharmPromoHash() {
  try {
    if (
      window.location.hash === CHARM_LANDING.hash &&
      window.history &&
      window.history.replaceState
    ) {
      const newUrl = window.location.pathname + window.location.search;
      window.history.replaceState(null, "", newUrl);
    }
  } catch (e) {
    console.warn("[CharmPromo] Unable to clean hash", e);
  }
}

async function maybeAutoAddCharmPromoFromLanding() {
  try {
    // Only run on one of the allowed target paths
    if (!isOnCharmLandingPath()) return;

    // Require the promo hash
    if (window.location.hash !== CHARM_LANDING.hash) return;

    // Avoid multiple adds per session
    if (sessionStorage.getItem(CHARM_LANDING.sessionFlag)) {
      cleanCharmPromoHash();
      return;
    }

    // Check if GWP is already in the cart
    const cart = await getCart();
    const alreadyInCart = cart.items.some(
      (item) => String(item.variant_id) === String(CHARM_LANDING.variantId)
    );

    if (alreadyInCart) {
      sessionStorage.setItem(CHARM_LANDING.sessionFlag, "true");
      cleanCharmPromoHash();
      return;
    }

    console.log(
      "[CharmPromo] Auto-adding charm promo variant from landing:",
      CHARM_LANDING.variantId
    );

    // Add the GWP variant
    const addRes = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({
        items: [
          {
            id: CHARM_LANDING.variantId,
            quantity: 1,
          },
        ],
      }),
    });

    if (!addRes.ok) {
      console.warn(
        "[CharmPromo] /cart/add.js returned non-OK",
        addRes.status
      );
    }

    sessionStorage.setItem(CHARM_LANDING.sessionFlag, "true");
    cleanCharmPromoHash();

    // Let SlideCart / others update if needed
    window.SLIDECART_UPDATE?.();

    // Optionally re-run enforcement soon after add
    scheduleCharmEnforcement(300);
  } catch (err) {
    console.error("[CharmPromo] Error auto-adding charm promo", err);
  }
}


// -------------------------------------------
// Enforcement logic (your existing code)
// -------------------------------------------

async function enforcePromoRules({ longWait = false } = {}) {
  console.log("[CharmPromo] Enforcing charm promo rules…");

  // Longer wait for the FIRST run (and optionally if we ever force longWait)
  const retries = longWait || firstCharmEnforcement ? 16 : 5;   // up to ~6.4s vs ~1.25s
  const delay   = longWait || firstCharmEnforcement ? 400 : 250;

  const cart = await getCartWithOptionalWait(retries, delay);
  firstCharmEnforcement = false;

  const activeCodes = (cart.discount_codes || []).map((dc) =>
    (dc.code || "").toUpperCase()
  );

  for (const item of cart.items) {
    const variantId = String(item.variant_id);

    // Only care about variants in our charm promo list
    if (!PROMO_VARIANT_IDS.includes(variantId)) continue;

    const promo = CHARM_PROMOS.find((p) => p.variantId === variantId);
    if (!promo) continue; // Safety guard

    const requiredCode = promo.requiredCode.toUpperCase();
    const hasRequiredCode = activeCodes.includes(requiredCode);

    // If the matching discount code is missing, remove the line item
    if (!hasRequiredCode) {
      console.log(
        `[CharmPromo] Removing variant ${variantId} — missing required code ${requiredCode}`
      );
      await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: item.key, quantity: 0 }),
      });

      window.SLIDECART_UPDATE?.();
      continue;
    }

    // Required code is present — enforce quantity == 1
    if (item.quantity !== 1) {
      console.log(
        `[CharmPromo] Normalizing quantity for ${variantId} from ${item.quantity} → 1`
      );
      await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: item.key, quantity: 1 }),
      });
      window.SLIDECART_UPDATE?.();
    }

    // Lock quantity controls in the UI for this specific variant
    disableQuantityControls(variantId);
  }
}

function disableQuantityControls(variantId) {
  // Slide-out cart (SlideCart HQ style)
  document
    .querySelectorAll(`[data-variant-id="${variantId}"]`)
    .forEach((line) => {
      const dec = line.querySelector('button[aria-label="decrease quantity"]');
      const inc = line.querySelector('button[aria-label="increase quantity"]');
      const input = line.querySelector(
        ".quantity-selector input[type='text'], .quantity-selector input[type='number']"
      );

      if (dec) {
        dec.disabled = true;
        dec.setAttribute("aria-disabled", "true");
      }
      if (inc) {
        inc.disabled = true;
        inc.setAttribute("aria-disabled", "true");
      }
      if (input) {
        input.readOnly = true;
        input.value = "1";
        input.classList.add("charm-promo-locked");
      }
    });

  // /cart page (Dawn-style IDs like updates_large_{{ variant.id }})
  document.querySelectorAll(".cart__qty-input").forEach((input) => {
    const match = input.id && input.id.match(/updates_large_(\d+)/);
    if (match && match[1] === String(variantId)) {
      input.readOnly = true;
      input.value = "1";
      input.classList.add("charm-promo-locked");
    }
  });
}

// --- Throttled enforcement helper ---
let charmEnforceTimeout;
function scheduleCharmEnforcement(delay = 300) {
  clearTimeout(charmEnforceTimeout);
  charmEnforceTimeout = setTimeout(() => enforcePromoRules(), delay);
}

// --- MutationObserver with debounce ---
const charmObserver = new MutationObserver(() => {
  scheduleCharmEnforcement(300);
});

charmObserver.observe(document.body, { childList: true, subtree: true });

// Initial run – force long wait on first load
document.addEventListener("DOMContentLoaded", () => {
  console.log("[CharmPromo] DOM ready — auto-add + enforcing charm promo rules…");
  // Fire the landing helper (no harm if URL/hash don't match)
  maybeAutoAddCharmPromoFromLanding();
  // Normal enforcement logic
  enforcePromoRules({ longWait: true });
});

// // ===========================================
// // Charm Promo Enforcement (Pierre + Welcome Bracelet)
// // ===========================================

// // Config: list of charm promos to enforce
// const CHARM_PROMOS = [
//   {
//     variantId: "46790830882982",      // Pierre + welcome bracelet bundle
//     requiredCode: "CHARMED2MEETU",    // Required discount code
//   },
//   {
//     variantId: "46715440890022",      // Second charm promo variant
//     requiredCode: "CHARMEDTOMEETYOU", // Required discount code
//   },
// ];

// const PROMO_VARIANT_IDS = CHARM_PROMOS.map((p) => p.variantId);

// let firstCharmEnforcement = true;

// async function getCart() {
//   const res = await fetch("/cart.js");
//   return res.json();
// }

// /**
//  * If no promo item is present, just return the cart immediately.
//  * If ANY promo item IS present, optionally wait for discount codes to settle.
//  */
// async function getCartWithOptionalWait(retries = 5, delay = 250) {
//   const initialCart = await getCart();
//   const hasPromoItem = initialCart.items.some((item) =>
//     PROMO_VARIANT_IDS.includes(String(item.variant_id))
//   );

//   if (!hasPromoItem) {
//     return initialCart;
//   }

//   // We do have at least one promo item — wait a bit for codes to appear, to avoid race conditions
//   for (let i = 0; i < retries; i++) {
//     const cart = await getCart();
//     const hasCode =
//       Array.isArray(cart.discount_codes) && cart.discount_codes.length > 0;
//     if (hasCode) return cart;
//     await new Promise((r) => setTimeout(r, delay));
//   }

//   // If we get here, codes never showed up — just return latest cart anyway
//   return getCart();
// }

// async function enforcePromoRules({ longWait = false } = {}) {
//   console.log("[CharmPromo] Enforcing charm promo rules…");

//   // Longer wait for the FIRST run (and optionally if we ever force longWait)
//   const retries = longWait || firstCharmEnforcement ? 16 : 5;   // up to ~6.4s vs ~1.25s
//   const delay   = longWait || firstCharmEnforcement ? 400 : 250;

//   const cart = await getCartWithOptionalWait(retries, delay);
//   firstCharmEnforcement = false;

//   const activeCodes = (cart.discount_codes || []).map((dc) =>
//     (dc.code || "").toUpperCase()
//   );

//   for (const item of cart.items) {
//     const variantId = String(item.variant_id);

//     // Only care about variants in our charm promo list
//     if (!PROMO_VARIANT_IDS.includes(variantId)) continue;

//     const promo = CHARM_PROMOS.find((p) => p.variantId === variantId);
//     if (!promo) continue; // Safety guard

//     const requiredCode = promo.requiredCode.toUpperCase();
//     const hasRequiredCode = activeCodes.includes(requiredCode);

//     // If the matching discount code is missing, remove the line item
//     if (!hasRequiredCode) {
//       console.log(
//         `[CharmPromo] Removing variant ${variantId} — missing required code ${requiredCode}`
//       );
//       await fetch("/cart/change.js", {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({ id: item.key, quantity: 0 }),
//       });

//       window.SLIDECART_UPDATE?.();
//       continue;
//     }

//     // Required code is present — enforce quantity == 1
//     if (item.quantity !== 1) {
//       console.log(
//         `[CharmPromo] Normalizing quantity for ${variantId} from ${item.quantity} → 1`
//       );
//       await fetch("/cart/change.js", {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({ id: item.key, quantity: 1 }),
//       });
//       window.SLIDECART_UPDATE?.();
//     }

//     // Lock quantity controls in the UI for this specific variant
//     disableQuantityControls(variantId);
//   }
// }

// function disableQuantityControls(variantId) {
//   // Slide-out cart (SlideCart HQ style)
//   document
//     .querySelectorAll(`[data-variant-id="${variantId}"]`)
//     .forEach((line) => {
//       const dec = line.querySelector('button[aria-label="decrease quantity"]');
//       const inc = line.querySelector('button[aria-label="increase quantity"]');
//       const input = line.querySelector(
//         ".quantity-selector input[type='text'], .quantity-selector input[type='number']"
//       );

//       if (dec) {
//         dec.disabled = true;
//         dec.setAttribute("aria-disabled", "true");
//       }
//       if (inc) {
//         inc.disabled = true;
//         inc.setAttribute("aria-disabled", "true");
//       }
//       if (input) {
//         input.readOnly = true;
//         input.value = "1";
//         input.classList.add("charm-promo-locked");
//       }
//     });

//   // /cart page (Dawn-style IDs like updates_large_{{ variant.id }})
//   document.querySelectorAll(".cart__qty-input").forEach((input) => {
//     const match = input.id && input.id.match(/updates_large_(\d+)/);
//     if (match && match[1] === String(variantId)) {
//       input.readOnly = true;
//       input.value = "1";
//       input.classList.add("charm-promo-locked");
//     }
//   });
// }

// // --- Throttled enforcement helper ---
// let charmEnforceTimeout;
// function scheduleCharmEnforcement(delay = 300) {
//   clearTimeout(charmEnforceTimeout);
//   charmEnforceTimeout = setTimeout(() => enforcePromoRules(), delay);
// }

// // --- MutationObserver with debounce ---
// const charmObserver = new MutationObserver(() => {
//   scheduleCharmEnforcement(300);
// });

// charmObserver.observe(document.body, { childList: true, subtree: true });

// // Initial run – force long wait on first load
// document.addEventListener("DOMContentLoaded", () => {
//   console.log("[CharmPromo] DOM ready — enforcing charm promo rules (long wait) …");
//   enforcePromoRules({ longWait: true });
// });
